import NiceModal from '@ebay/nice-modal-react';
import { enqueueSnackbar } from 'notistack';
import { includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getMessage } from '@gef-ui/localization/messages';

import MODALS from 'constants/modals';
import { KCL_DOCUMENT_CHECK_ADMINS } from 'features/common/acl/constants';
import fetch from 'features/common/fetch';
import { urls } from 'features/common/fetch/constants';
import { FIELDS, NOTICES_FORM_NAME } from 'features/views/productionControlDetail/notice/form';

import { fetchDocumentDetail } from '../actions';
import { getProductionControlDetail, getProductionControlDetailNotices } from '../selectors';
import { setProductionControlDetailNotices } from '../slice';
import { CANNOT_ADD_KOPR_FEEDBACK_PRODUCTION_ALREADY_CLOSED_ERR_MSG } from './constants';

export const fetchDocumentDetailNotices = (documentId) => (dispatch) => {
	dispatch(
		fetch.actions.get(fetch.constants.urls.koprDetailNotices, {
			replaceParams: { documentId },
			onSuccess: [
				(response) => () => {
					if (!response || !response.body) return;
					const body = response?.body;
					dispatch(setProductionControlDetailNotices(body));
				},
			],
		}),
	);
};

export const openVerifyNoticeStateIdLightBoxOrSaveNotice = (stateId) => (dispatch) => {
	if (includes(stateId, [2, 3])) {
		let finalMsg;

		switch (stateId) {
			case 2:
				finalMsg = 'lightBox:productionControlNoticeStateId2';
				break;
			case 3:
				finalMsg = 'lightBox:productionControlNoticeStateId3';
				break;
		}

		NiceModal.show(MODALS.lightbox_modal, {
			title: getMessage(finalMsg),
			onConfirm: () => dispatch(saveNewNotice()),
		});
	} else {
		dispatch(saveNewNotice());
	}
};

export const startSaveNewNotice =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const userIsCentral = hasPermissionByIds(getState())(KCL_DOCUMENT_CHECK_ADMINS);
		const data = await getFormData(NOTICES_FORM_NAME);

		if (userIsCentral) {
			dispatch(saveNewNotice());
		} else {
			dispatch(openVerifyNoticeStateIdLightBoxOrSaveNotice(Number(data?.documentState)));
		}
	};

export const saveNewNotice =
	() =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const userIsCentral = hasPermissionByIds(getState())(KCL_DOCUMENT_CHECK_ADMINS);
		const detail = getProductionControlDetail(getState());
		const formData = await getFormData(NOTICES_FORM_NAME);

		const documentIsNotClosed = detail?.solutionStateId !== 3;
		const documentInNotFoundState = detail?.documentState === 1;
		const showState = !userIsCentral && documentInNotFoundState && documentIsNotClosed;
		const showCentralConsent = userIsCentral && documentInNotFoundState;
		const notices = getProductionControlDetailNotices(getState());

		const generateStateId = (formData) => {
			if (userIsCentral) {
				return formData?.centralConsent ? Number(formData?.centralConsent) : null;
			} else {
				return formData?.documentState ? Number(formData?.documentState) : null;
			}
		};

		const stateId = generateStateId(formData);

		dispatch(
			fetch.actions.post(urls.koprNewNotice, {
				body: {
					documentId: detail?.id,
					notice: formData?.noticeTextContent,
					stateId,
				},
				onSuccess: [
					() => () => {
						dispatch(fetchDocumentDetail(detail?.id));
						dispatch(fetchDocumentDetailNotices(detail?.id));
						sendControlNext(
							NOTICES_FORM_NAME,
							setValue({ fieldName: FIELDS.noticeTextContent, value: null }),
						);
					},
				],
				onError: [
					({ failure }) =>
						() => {
							if (
								failure?.status === 400 &&
								failure?.parsedContent.message ===
									CANNOT_ADD_KOPR_FEEDBACK_PRODUCTION_ALREADY_CLOSED_ERR_MSG
							) {
								enqueueSnackbar(getMessage('toastr:cannotAddKoprFeedbackProductionAlreadyClosed'), {
									variant: 'error',
								});

								dispatch(fetchDocumentDetail(detail?.id));
								dispatch(fetchDocumentDetailNotices(detail?.id));
								dispatch(
									prefillLastDocumentStateToStateOrCentralConsent({
										showState,
										showCentralConsent,
										notices,
									}),
								);
							}
						},
				],
				shouldReportError: ({ failure }) => {
					return !(
						failure.status === 400 &&
						failure?.parsedContent.message === CANNOT_ADD_KOPR_FEEDBACK_PRODUCTION_ALREADY_CLOSED_ERR_MSG
					);
				},
			}),
		);
	};

export const prefillLastDocumentStateToStateOrCentralConsent =
	({ showState, showCentralConsent, notices }) =>
	(dispatch, getState, { sendControlNext }) => {
		if (!isNilOrEmpty(notices)) {
			const [lastNotice] = notices;

			if (showState && lastNotice?.stateId) {
				sendControlNext(
					NOTICES_FORM_NAME,
					setValue({ fieldName: FIELDS.documentState, value: String(lastNotice.stateId) }),
				);
			}

			if (showCentralConsent && lastNotice?.finalStateId) {
				sendControlNext(
					NOTICES_FORM_NAME,
					setValue({ fieldName: FIELDS.centralConsent, value: String(lastNotice.finalStateId) }),
				);
			}
		}
	};
